import { useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Stack,
  Card,
  CardContent,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// hooks
import useAuth from '../../../../hooks/useAuth';
import useResponsive from '../../../../hooks/useResponsive';
// components
import {
  PositionLabel,
} from '../../';
import DIOText from '../../../../components/DIOText';
import Iconify from '../../../../components/Iconify';
import Image from '../../../../components/Image';
import SvgIconStyle from '../../../../components/SvgIconStyle';
// utils
import axios from '../../../../utils/axios';

export default function SelectMethodForm({ jobPost, next, back, close }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  const [postDirect, setPostDirect] = useState(false);
  const [postPaste, setPostPaste] = useState(false);

  const goBack = () => {
    back();
  };

  const selectDirect = async () => {
    try {
      setPostDirect(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/direct/`);
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      setPostDirect(false);
      await next(jobPostId, 'direct');
    } catch (err) {
      setPostDirect(false);
      console.error(err);
      enqueueSnackbar('처리중 오류가 발생하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  }

  const selectPaste = async () => {
    try {
      setPostPaste(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/paste/`);
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      setPostPaste(false);
      await next(jobPostId, 'paste');
    } catch (err) {
      setPostPaste(false);
      console.error(err);
      enqueueSnackbar('처리중 오류가 발생하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: smUp ? 6 : 2 }}>
        <IconButton color="inherit" edge="start" onClick={goBack}>
          <Iconify icon="ic:chevron-left" sx={{ width: smUp ? 32 : 24 , height: smUp ? 32 : 24, color: (theme) => theme.palette.primary.main }} />
        </IconButton>
        <IconButton color="inherit" edge="end" onClick={close}>
          <Iconify icon="ic:round-close" sx={{ width: smUp ? 32 : 24 , height: smUp ? 32 : 24, color: (theme) => theme.palette.text.disabled }} />
        </IconButton>
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <PositionLabel position={jobPost?.position} detailRole={jobPost?.detail_role} aiSelected={jobPost?.select_ai_position} />
        <Typography variant="h4">
          공고 작성 방법을
        </Typography>
        <Typography variant="h4">
          선택해주세요.
        </Typography>
      </Stack>

      <Stack spacing={3} sx={{ mt: 4, width: 1 }}>
        <Card
          sx={{
            width: 1,
            '&:hover': {
              cursor: 'pointer',
              boxShadow: (theme) => theme.customShadows.z24,
            },
            '&:active': {
              backgroundColor: (theme) => theme.palette.grey[200],
            },
          }}
          onClick={selectDirect}
        >
          <CardContent sx={{ py: 3, px: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                mb: 1,
                bgcolor: (theme) => alpha(theme.palette.oldprimary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              component="span"
            >
              <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ width: 16, height: 16, color: "oldprimary.main" }} />
            </Box>
            <Typography variant="h5" sx={{ wordBreak: "keep-all" }}>
              <DIOText /> 채용 공고 작성하기
            </Typography>
            <Stack direction="row" alignItems={"center"} justifyContent="flex-end" spacing={0.5} sx={{ mt: {xs : 3, sm: 0} }}>
              {postDirect && (
                <CircularProgress sx={{ color: "oldprimary.main", mr: 4 }} size={24} thickness={5} />
              )}
              {!postDirect && (
              <>
              <Typography variant="subtitle1" sx={{ color: "oldprimary.main" }}>
                공고 작성하기
              </Typography>
              <Iconify
                icon="ic:baseline-chevron-right"
                sx={{ width: 24, height: 24, color: "oldprimary.main" }}
              />
              </>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card
          sx={{
            width: 1,
            '&:hover': {
              cursor: 'pointer',
              boxShadow: (theme) => theme.customShadows.z24,
            },
            '&:active': {
              backgroundColor: (theme) => theme.palette.grey[200],
            },
          }}
          onClick={selectPaste}
        >
          <CardContent sx={{ py: 3, px: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                mb: 1,
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              component="span"
            >
              <SvgIconStyle src={`/icons/icon-link-mono.svg`} sx={{ width: 16, height: 16, color: "secondary.main" }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              다른 사이트 채용 공고 링크 붙여넣기
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "keep-all", color: "text.secondary" }} paragraph>
              다른 곳에서 이미 채용 공고를 작성했나요? 링크만 붙여 넣으면 채용 공고 작성을 완료할 수 있어요.
            </Typography>
            {!smUp && (
            <Image
              sx={{ width: '253px', height: '32px' }}
              visibleByDefault
              disabledEffect
              src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/other-services.png"
            />
            )}
            <Stack direction="row" alignItems={"center"} justifyContent={smUp ? "space-between" : "flex-end"} spacing={0.5} sx={{ mt: {xs : 3, sm: 0} }}>
              {smUp && (
                <Image
                  sx={{ width: '253px', height: '32px' }}
                  visibleByDefault
                  disabledEffect
                  src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/other-services.png"
                />
              )}
              <Stack direction="row" alignItems={"center"} spacing={0.5}>
                {postPaste && (
                  <CircularProgress sx={{ color: "secondary.main", mr: 4 }} size={24} thickness={5} />
                )}
                {!postPaste && (
                <>
                  <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                    링크 붙여넣기
                  </Typography>
                  <Iconify
                    icon="ic:baseline-chevron-right"
                    sx={{ width: 24, height: 24, color: "secondary.main" }}
                  />
                </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  )
}